<script setup>
import { ref } from 'vue'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const sections = ref([
  { id: 'introducao', title: 'Introdução', subtitle: 'Informações gerais sobre a plataforma e finalidade deste documento.' },
  { id: 'termos', title: 'I. Entenda os principais termos utilizados nesta Política' },
  { id: 'dados-tratados', title: 'II. Dados Pessoais Tratados pela Eventos Connect' },
  { id: 'finalidades', title: 'III. Por que estamos tratando os seus dados pessoais?' },
  { id: 'agentes', title: 'IV. Agentes de tratamento envolvidos' },
  { id: 'direitos', title: 'V. Direitos do titular de dados pessoais' },
  { id: 'compartilhamento', title: 'VI. Compartilhamento de dados com terceiros' },
  { id: 'retencao', title: 'VII. Prazo de retenção e descarte de dados pessoais' },
  { id: 'disposicoes', title: 'VIII. Disposições gerais' },
])

const scrollToSection = sectionId => {
  const element = document.getElementById(sectionId)
  if (element)
    element.scrollIntoView({ behavior: 'smooth' })
}
</script>

<template>
  <VApp>
    <VMain>
      <VContainer
        fluid
        class="pa-0"
      >
        <!-- Header -->
        <VCard
          flat
          class="bg-primary text-white"
        >
          <VContainer>
            <VRow justify="center">
              <VCol
                cols="12"
                md="10"
                lg="8"
              >
                <div class="text-center py-8">
                  <h1 class="text-h3 font-weight-bold mb-4">
                    POLÍTICA DE PRIVACIDADE
                  </h1>
                  <VChip
                    color="orange"
                    class="mb-4"
                    variant="flat"
                  >
                    <strong>Última atualização: 20 de maio de 2025.</strong>
                  </VChip>
                  <p class="text-h6 mb-0">
                    Sua privacidade importa para a MKT Connect.
                  </p>
                </div>
              </VCol>
            </VRow>
          </VContainer>
        </VCard>

        <VContainer>
          <VRow>
            <!-- Sidebar com Índice -->
            <VCol
              cols="12"
              md="3"
              class="d-none d-md-block"
            >
              <VCard
                class="sticky-top"
                style="inset-block-start: 20px;"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-list
                  </VIcon>
                  Índice
                </VCardTitle>
                <VList density="compact">
                  <VListItem
                    v-for="section in sections"
                    :key="section.id"
                    class="cursor-pointer"
                    @click="scrollToSection(section.id)"
                  >
                    <VListItemTitle class="text-body-2">
                      {{ section.title }}
                    </VListItemTitle>
                    <VListItemSubtitle
                      v-if="section.subtitle"
                      class="text-caption"
                    >
                      {{ section.subtitle }}
                    </VListItemSubtitle>
                  </VListItem>
                </VList>
              </VCard>
            </VCol>

            <!-- Conteúdo Principal -->
            <VCol
              cols="12"
              md="9"
            >
              <!-- Introdução -->
              <VCard
                id="introducao"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-info-circle
                  </VIcon>
                  Introdução
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    Esta Política de Privacidade tem como finalidade apresentar, de forma clara e objetiva, como a MKT CONNECT LTDA
                    ("Eventos Connect" ou "Plataforma"), pessoa jurídica de direito privado, inscrita no CNPJ sob o nº 01.945.991/0001
                    48, com endereço fiscal à Alameda do Inga, 585, Apt 1901, torre 8, Vale Sereno, na Cidade de Nova Lima, Estado de
                    Minas Gerais, doravante denominada "Empresa" ou "Plataforma" realiza o tratamento de dados pessoais dos usuários que
                    acessam e utilizam os seus ambientes digitais.
                  </p>
                  <p class="text-body-1 mb-4">
                    Esta Política de Privacidade se aplica a todas as pessoas físicas ou jurídicas que acessam, navegam ou utilizam os
                    ambientes digitais da plataforma Eventos Connect, especialmente aquelas interessadas em conhecer, contratar ou
                    utilizar os serviços oferecidos por meio da intermediação entre Organizadores de Eventos e Anunciantes ou
                    Fornecedores de serviços e/ou espaços.
                  </p>
                  <VAlert
                    type="info"
                    class="mb-4"
                  >
                    <p class="mb-2">
                      Ao se cadastrar, acessar ou utilizar os recursos da Eventos Connect, você seja na condição de Usuário Organizador
                      ou Anunciante/Fornecedor declara que leu, compreendeu e concorda com os termos desta Política de Privacidade,
                      elaborada em conformidade com a Lei nº 13.709/2018 - Lei Geral de Proteção de Dados Pessoais (LGPD).
                    </p>
                  </VAlert>
                  <p class="text-body-1 mb-4">
                    Nosso site pode conter links para outros sites que não são operados por nós. Por isso, é importante estar atento às
                    políticas de privacidade desses ambientes, pois não temos controle sobre como esses terceiros tratam suas
                    informações.
                  </p>
                  <p class="text-body-1 mb-4">
                    Você sempre terá o direito de não fornecer determinados dados, ciente de que, em alguns casos, isso pode limitar o
                    acesso a conteúdo ou funcionalidades da plataforma.
                  </p>
                  <p class="text-body-1 mb-4">
                    Ao continuar navegando em nosso site e/ou utilizando nossos serviços, entendemos que você está de acordo com esta
                    política. Se tiver qualquer dúvida sobre como tratamos suas informações, estamos à disposição para conversar.
                  </p>
                </VCardText>
              </VCard>

              <!-- Seção I - Termos -->
              <VCard
                id="termos"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-book
                  </VIcon>
                  I. ENTENDA OS PRINCIPAIS TERMOS UTILIZADOS NESTA POLÍTICA DE PRIVACIDADE
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    1.1. Para facilitar o entendimento desta Política de Privacidade, considera-se:
                  </p>

                  <VExpansionPanels class="mb-4">
                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(i) "Anunciante" ou "Fornecedor de serviços e/ou espaços"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        pessoa física ou jurídica, profissional autônomo ou
                        empresa, que realiza cadastro na plataforma Eventos Connect com a finalidade de divulgar serviços, produtos ou
                        espaços voltados à realização de eventos corporativos e sociais.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(ii) "Usuário Organizador" ou "Organizador de Evento"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        pessoa física ou jurídica, profissional ou eventual, que
                        realiza o cadastro na plataforma Eventos Connect com a finalidade de planejar e gerenciar eventos, bem como buscar,
                        comparar e contratar fornecedores de serviços e/ou espaços.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(iii) "Usuário"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        termo genérico que abrange o Organizador de Evento, o Anunciante ou Fornecedor de serviços e/ou
                        espaços, bem como qualquer pessoa que acesse ou utilize os serviços e funcionalidades da plataforma
                        Eventos Connect, com a finalidade de navegar, consultar, interagir ou contratar os recursos disponibilizados.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(iv) "Tratamento de Dados"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        toda e qualquer operação realizada pela Eventos Connect com Dados Pessoais, incluindo,
                        entre outras: coleta, armazenamento, avaliação, classificação, processamento, modificação, reprodução,
                        compartilhamento, transmissão, extração ou eliminação.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(v) "Dados Pessoais de Organizadores de Evento"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        são as informações fornecidas ou geradas pelo Organizador no
                        momento do cadastro e durante o uso da plataforma Eventos Connect, tais como: nome completo, CPF, RG (se pessoa
                        física), e-mail, telefone, dados de localização, tipo e porte do evento, cronograma, lista de convidados, orçamentos
                        solicitados, avaliações realizadas, endereço IP, data e hora de acesso e uso da plataforma.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(vi) "Dados Pessoais de Anunciantes ou Fornecedores"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        são as informações fornecidas pelo
                        Anunciante no momento do cadastro e durante o uso da plataforma Eventos Connect, incluindo: nome ou razão social,
                        nome fantasia, CPF ou CNPJ, e-mail, telefone, endereço comercial, localização de atendimento, redes sociais,
                        descrição dos serviços, fotos, vídeos, dados bancários (para cobrança e emissão de nota fiscal), endereço IP,
                        navegador utilizado, data e hora de acesso.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(vii) "Dados Pessoais"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        qualquer informação que permita identificar uma pessoa natural, de forma direta ou
                        indireta, como: nome, e-mail, número de documentos, telefone, endereço, geolocalização, imagem, voz, endereço IP e
                        outros dados fornecidos ou coletados durante o uso da plataforma.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(viii) "Avaliação de serviços"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        funcionalidade da plataforma que permite ao Organizador de Evento avaliar os
                        serviços prestados pelos Anunciantes ou Fornecedores com quem tenha contratado, com base em sua experiência. As
                        avaliações podem ser exibidas publicamente no perfil do fornecedor, conforme as regras da plataforma.
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <strong>(ix) "Plano contratado"</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        modelo de serviço escolhido pelo Anunciante ou Fornecedor dentro das opções oferecidas
                        pela plataforma, definindo o nível de funcionalidades disponíveis, visibilidade do perfil, recursos de divulgação e
                        valores aplicáveis, podendo incluir planos básico, premium ou personalizados.
                      </VExpansionPanelText>
                    </VExpansionPanel>
                  </VExpansionPanels>
                </VCardText>
              </VCard>

              <!-- Seção II - Dados Tratados -->
              <VCard
                id="dados-tratados"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-database
                  </VIcon>
                  II. DADOS PESSOAIS TRATADOS PELA EVENTOS CONNECT
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    2.1. A Eventos Connect realiza o tratamento de Dados Pessoais conforme o perfil e a interação do Usuário com a
                    plataforma, seja como Organizador de Evento ou Anunciante/Fornecedor. Cada funcionalidade pode demandar o
                    fornecimento ou coleta de dados específicos, respeitando o princípio da minimização previsto na LGPD. Os dados podem ser coletados:
                  </p>

                  <VList class="mb-4">
                    <VListItem>
                      <template #prepend>
                        <VIcon color="primary">
                          tabler-user
                        </VIcon>
                      </template>
                      <VListItemTitle>diretamente do Usuário;</VListItemTitle>
                    </VListItem>
                    <VListItem>
                      <template #prepend>
                        <VIcon color="primary">
                          tabler-users
                        </VIcon>
                      </template>
                      <VListItemTitle>fornecidos por terceiros; ou</VListItemTitle>
                    </VListItem>
                    <VListItem>
                      <template #prepend>
                        <VIcon color="primary">
                          tabler-world
                        </VIcon>
                      </template>
                      <VListItemTitle>coletados automaticamente durante a navegação na Plataforma por meio de cookies ou outras tecnologias.</VListItemTitle>
                    </VListItem>
                  </VList>

                  <p class="text-body-1 mb-4">
                    2.2. Esta política de privacidade regula o tratamento de dados pessoais realizado por meio dos seguintes canais e
                    ferramentas:
                  </p>

                  <VRow class="mb-4">
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VCard variant="outlined">
                        <VCardText>
                          <VIcon
                            color="primary"
                            class="mb-2"
                          >
                            tabler-world
                          </VIcon>
                          <p class="text-body-2 mb-0">
                            (i) Landing pages utilizadas para apresentar, divulgar ou promover os serviços e funcionalidades da plataforma;
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VCard variant="outlined">
                        <VCardText>
                          <VIcon
                            color="primary"
                            class="mb-2"
                          >
                            tabler-apps
                          </VIcon>
                          <p class="text-body-2 mb-0">
                            (ii) ambientes internos da plataforma utilizados para criar perfis públicos, cadastrar eventos, divulgar serviços e
                            realizar interações;
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VCard variant="outlined">
                        <VCardText>
                          <VIcon
                            color="primary"
                            class="mb-2"
                          >
                            tabler-forms
                          </VIcon>
                          <p class="text-body-2 mb-0">
                            (iii) formulários eletrônicos de cadastro, interesse ou contato;
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VCard variant="outlined">
                        <VCardText>
                          <VIcon
                            color="primary"
                            class="mb-2"
                          >
                            tabler-credit-card
                          </VIcon>
                          <p class="text-body-2 mb-0">
                            (iv) Processos relacionados à contratação de planos de assinatura pelos Anunciantes ou uso do planner pelos
                            Organizadores;
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                    <VCol cols="12">
                      <VCard variant="outlined">
                        <VCardText>
                          <VIcon
                            color="primary"
                            class="mb-2"
                          >
                            tabler-mail
                          </VIcon>
                          <p class="text-body-2 mb-0">
                            (v) Ferramentas de e-mail marketing, CRM e automações, usadas para envio de mensagens, suporte e relacionamento com
                            os usuários da plataforma;
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                  </VRow>

                  <VDivider class="my-4" />

                  <p class="text-body-1 mb-4">
                    2.3. Dados Pessoais fornecidos diretamente pelo Usuário. Ao criar uma conta, configurar a loja virtual, preencher
                    informações do evento ou interagir com nosso atendimento, o Usuário poderá fornecer os seguintes dados: nome
                    completo, CPF ou CNPJ, e-mail, telefone, endereço, localização de atendimento, dados do evento, fotos, vídeos,
                    descrição dos serviços e, quando aplicável, dados bancários para emissão de nota fiscal.
                  </p>

                  <p class="text-body-1 mb-4">
                    2.4. Dados Pessoais inseridos por terceiros na Plataforma. A Eventos Connect poderá tratar Dados Pessoais inseridos
                    por Organizadores ou Anunciantes com a finalidade de viabilizar interações entre as partes. Isso inclui: nome
                    completo, CPF, e-mail, telefone celular, localização, data do evento, dados de acesso e dados inseridos em
                    formulários de contato ou de orçamentos.
                  </p>

                  <p class="text-body-1 mb-4">
                    2.5. Dados Pessoais coletados automaticamente. Durante o uso da Plataforma, podem ser coletados automaticamente:
                    endereço IP, navegador utilizado, data e hora de acesso, localização aproximada, ações realizadas, tipo de
                    dispositivo, provedor de conexão, sistema operacional, entre outros dados técnicos.
                  </p>

                  <VAlert
                    type="warning"
                    class="mb-4"
                  >
                    <strong>2.6. Uso de Cookies.</strong> A Plataforma utiliza cookies pequenos arquivos armazenados no dispositivo do Usuário para
                    personalizar a navegação e melhorar a experiência do visitante. São coletadas informações como tipo de navegador, tempo de navegação, páginas acessadas,
                    idioma e outras preferências. O Usuário pode configurar suas preferências no banner de cookies ou optar por
                    bloqueá-los, ciente de que certas funcionalidades podem ser prejudicadas. O uso de cookies segue os termos da Lei nº
                    13.709/2018 (LGPD).
                  </VAlert>

                  <p class="text-body-1 mb-4">
                    Em caso de dúvidas sobre o conteúdo desta política ou sobre como usamos seus dados pessoais, você pode entrar em
                    contato conosco, cujas informações estão disponíveis no final deste documento.
                  </p>
                </VCardText>
              </VCard>

              <!-- Seção III - Finalidades -->
              <VCard
                id="finalidades"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-target
                  </VIcon>
                  III. PORQUE ESTAMOS TRATANDO OS SEUS DADOS PESSOAIS?
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    3.1. A MKT Connect, por meio da plataforma Eventos Connect, trata os seus dados pessoais com seriedade e
                    responsabilidade, em conformidade com a Lei Geral de Proteção de Dados Pessoais (LGPD - Lei nº 13.709/2018, art.
                    9º).
                  </p>

                  <p class="text-body-1 mb-4">
                    3.2. As informações coletadas são utilizadas de forma adequada, limitada ao necessário, e com finalidades
                    específicas, legítimas e informadas, de acordo com o perfil do usuário (Organizador de Evento ou
                    Anunciante/Fornecedor de serviços e/ou espaços).
                  </p>

                  <!-- Finalidades Organizadores -->
                  <VCard
                    class="mb-6"
                    variant="outlined"
                  >
                    <VCardTitle class="bg-primary">
                      <VIcon class="me-2">
                        tabler-calendar-user
                      </VIcon>
                      Finalidade do tratamento - Organização de Eventos
                    </VCardTitle>
                    <VCardText>
                      <VList>
                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">i</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Uso de planner digital
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Seus dados permitem que você utilize o planner uma ferramenta exclusiva da Eventos
                            Connect para organizar cada etapa do seu evento: desde a criação do cronograma até a gestão de lista de convidados,
                            orçamentos e fornecedores.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">ii</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Pesquisa e contato com fornecedores
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Tratamos suas informações para que você possa buscar prestadores de
                            serviços de acordo com as suas necessidades, visualizar os perfis e iniciar contato diretamente com os fornecedores
                            que mais combinam com o seu evento.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">iii</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Envio de solicitações de orçamento
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Seus dados são usados para que você possa solicitar orçamentos com
                            agilidade, facilitando o contato com os Anunciantes e o comparativo de proposta.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">iv</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Comunicação e suporte
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Utilizamos seus dados para enviar informações importantes sobre o uso da plataforma,
                            atualizações, lembretes e também para prestar suporte quando você precisar de ajuda.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">v</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Avaliação de fornecedores
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Caso você opte por avaliar os serviços de um Anunciante com quem interagiu, seus
                            dados serão utilizados para registrar e associar essa avaliação ao seu perfil de usuário.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">vi</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Obrigações legais e fiscais
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Como toda empresa séria, a MKT Connect também precisa cumprir deveres legais e
                            fiscais. Por isso, alguns dados podem ser utilizados para emitir notas fiscais, manter registros obrigatórios, prestar contas aos órgãos
                            competentes ou atender a determinações legais, quando necessário. Isso faz parte do nosso compromisso com a
                            transparência e a conformidade.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">vii</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Enviar comunicações de marketing
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Desenvolver e executar ações de marketing, como apoiar e promover os
                            serviços oferecidos pela plataforma; exibir conteúdo e anúncios personalizados em mídias pagas, como Google Ads e
                            Meta Ads; enviar comunicações comerciais, promocionais e atualizações sobre funcionalidades e recursos, respeitando
                            sempre o direito do titular de optar por não receber comunicações de marketing a qualquer tempo; realizar análises
                            estatísticas, estudos internos e pesquisas sobre o uso da plataforma; e enviar conteúdos informativos e educativos
                            relacionados à organização de eventos e ao uso da Eventos Connect. Sempre garantindo o seu direito de cancelar ou
                            recusar esses envios a qualquer momento.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">viii</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Melhorias na navegação e na experiência de uso
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Coletamos alguns dados técnicos para entender como você usa
                            o site: o que funciona bem, o que pode ser melhorado, e como tornar tudo mais rápido e prático. Isso nos ajuda a
                            adaptar a plataforma para que ela atenda melhor às suas necessidades.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="blue"
                              size="small"
                            >
                              <span class="text-white text-caption">ix</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Desenvolvimento e inovação dos nossos Serviços
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Além disso, usamos informações para analisar como estamos
                            indo, planejar melhorias, testar novas soluções e seguir crescendo com segurança e responsabilidade. Esses dados nos
                            ajudam a evoluir e entregar um serviço cada vez mais completo e eficiente para você e para outros alunos.
                          </VListItemSubtitle>
                        </VListItem>
                      </VList>
                    </VCardText>
                  </VCard>

                  <!-- Finalidades Fornecedores -->
                  <VCard
                    class="mb-6"
                    variant="outlined"
                  >
                    <VCardTitle class="bg-primary">
                      <VIcon class="me-2">
                        tabler-store
                      </VIcon>
                      3.3. Finalidade de Tratamento Fornecedores Anunciante
                    </VCardTitle>
                    <VCardText>
                      <VList>
                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">i</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Etapas antes da Compra dos nosso Produtos
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Usamos seus dados para criar sua conta e garantir que você possa
                            acessar a plataforma de forma segura, com login individual e protegido.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">x</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Cadastro e acesso à Plataforma
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Após a finalização do seu cadastro, usamos seus dados para criar seu login de
                            acesso, ativar o seu perfil como anunciante e liberar as funcionalidades do plano contratado. Isso inclui o acesso à
                            área administrativa da sua loja virtual, onde você poderá configurar sua vitrine, atualizar suas informações e
                            gerenciar sua presença na plataforma de forma segura e personalizada.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">xi</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Criação e exibição da loja virtual personalizada
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Seus dados são usados para criar e manter sua loja virtual
                            dentro da Eventos Connect, onde você pode apresentar seus serviços da forma que preferir incluindo fotos, vídeos,
                            descrição do que oferece, diferenciais, informações de contato e localização. Essa vitrine é acessível para organizadores de eventos e visitantes da plataforma, conforme o plano
                            que você contratou.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">xii</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Processamento de planos contratados e emissão de documentos fiscais
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Quando você contrata um plano na Eventos
                            Connect, usamos seus dados para gerenciar sua assinatura, acompanhar o status do serviço contratado e emitir
                            documentos fiscais, como notas fiscais eletrônicas. O pagamento é feito por meio de plataformas intermediadoras, e
                            os dados são tratados com a finalidade de garantir o cumprimento das obrigações legais e comerciais.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">xiii</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Avaliações recebidas de organizadores
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Caso um organizador avalie seus serviços, seus dados serão utilizados
                            para associar essa avaliação ao seu perfil público, conforme as regras da plataforma.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">xiv</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Obrigações Legais e fiscais
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Como toda empresa séria, a MKT Connect também precisa cumprir deveres legais e
                            fiscais. Por isso, alguns dados podem ser utilizados para emitir notas fiscais, manter registros obrigatórios,
                            prestar contas aos órgãos competentes ou atender a determinações legais, quando necessário. Isso faz parte do nosso
                            compromisso com a transparência e a conformidade.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">xv</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Enviar comunicações de marketing
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Informações sobre nossos conteúdos, ofertas e campanhas relacionadas aos
                            produtos ou serviços disponibilizados pela MKT Connect similares, sugestão de melhorias sempre garantindo o seu
                            direito de cancelar ou recusar esses envios a qualquer momento.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">xvi</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Melhorias na navegação e na experiência de uso
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Coletamos alguns dados técnicos para entender como você usa o
                            site: o que funciona bem, o que pode ser melhorado, e como tornar tudo mais rápido e prático. Isso nos ajuda a
                            adaptar a plataforma para que ela atenda melhor às suas necessidades.
                          </VListItemSubtitle>
                        </VListItem>

                        <VListItem>
                          <template #prepend>
                            <VAvatar
                              color="green"
                              size="small"
                            >
                              <span class="text-white text-caption">xvii</span>
                            </VAvatar>
                          </template>
                          <VListItemTitle class="font-weight-medium">
                            Desenvolvimento e inovação dos nossos Serviços
                          </VListItemTitle>
                          <VListItemSubtitle>
                            Além disso, usamos informações para analisar como estamos
                            indo planejar melhorias, testar novas soluções e seguir crescendo com segurança e responsabilidade. Esses dados nos
                            ajudam a evoluir e entregar um serviço cada vez mais completo e eficiente para você e para outros alunos.
                          </VListItemSubtitle>
                        </VListItem>
                      </VList>
                    </VCardText>
                  </VCard>
                </VCardText>
              </VCard>

              <!-- Seção IV - Agentes -->
              <VCard
                id="agentes"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-users
                  </VIcon>
                  IV. AGENTES DE TRATAMENTOS ENVOLVIDOS
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    4.1. Nos termos da Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais LGPD), os agentes envolvidos no
                    tratamento de dados pessoais no contexto da Eventos Connect são definidos da seguinte forma:
                  </p>

                  <VRow class="mb-4">
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VCard
                        variant="outlined"
                        class="h-100"
                      >
                        <VCardTitle class="bg-blue-lighten-4">
                          <VIcon class="me-2">
                            tabler-user-check
                          </VIcon>
                          Controladora
                        </VCardTitle>
                        <VCardText>
                          <p class="text-body-2">
                            (i) "Controladora": é a MKT Connect, empresa responsável por tomar as decisões sobre o tratamento dos dados
                            pessoais coletados diretamente nos ambientes digitais da plataforma Eventos Connect.
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VCard
                        variant="outlined"
                        class="h-100"
                      >
                        <VCardTitle class="bg-green-lighten-4">
                          <VIcon class="me-2">
                            tabler-user-cog
                          </VIcon>
                          Operadores
                        </VCardTitle>
                        <VCardText>
                          <p class="text-body-2">
                            (ii) "Operadores": são prestadores de serviço contratados pela MKT Connect que tratam dados pessoais em nome da
                            empresa, de forma controlada e orientada, para cumprir as finalidades informadas nesta Política. Isso pode incluir,
                            por exemplo, empresas responsáveis pela hospedagem da plataforma, ferramentas de e-mail marketing, CRM, suporte
                            técnico e analytic.
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                  </VRow>

                  <VAlert
                    type="info"
                    class="mb-4"
                  >
                    <strong>4.2. Informações sobre os dados de pagamento.</strong> A MKT Connect não coleta, armazena ou tem acesso aos dados do seu
                    cartão de crédito, conta bancária ou qualquer outra informação financeira utilizada na contratação de planos ou
                    serviços.
                  </VAlert>

                  <p class="text-body-1 mb-4">
                    4.2.1. Todos os dados de pagamento são coletados e tratados exclusivamente pela plataforma intermediadora de
                    pagamentos utilizada para processar a transação. Essa plataforma é responsável por garantir a segurança,
                    criptografia e tratamento adequado dessas informações, conforme suas próprias políticas de privacidade e termos de
                    uso.
                  </p>

                  <p class="text-body-1 mb-4">
                    Para saber mais sobre como seus dados de pagamento são tratados, recomendamos que você acesse diretamente o site da
                    plataforma intermediadora e consulte sua Política de Privacidade.
                  </p>
                </VCardText>
              </VCard>

              <!-- Seção V - Direitos -->
              <VCard
                id="direitos"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-shield-check
                  </VIcon>
                  V. DIREITO DO TITULAR DE DADOS PESSOAIS (ART. 18. DÁ LGPD)
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    5.1. Você como titular dos seus próprios dados tem uma série de direitos garantidos pela Lei Geral de Proteção de
                    Dados Pessoais (LGPD Lei nº 13.709/2018). Esses direitos podem ser exercidos a qualquer momento, mediante
                    solicitação à MKT Connect, por meio dos canais informados no final desta política.:
                  </p>

                  <VRow>
                    <VCol
                      v-for="(direito, index) in [
                        { icon: 'tabler-help-circle', title: 'Confirmação do tratamento', desc: 'você pode nos perguntar se estamos tratando ou não algum dado pessoal seu;' },
                        { icon: 'tabler-eye', title: 'Acesso aos seus dados', desc: 'você pode solicitar uma cópia dos seus dados pessoais que estejam sob nossa responsabilidade;' },
                        { icon: 'tabler-pencil', title: 'Correção de dados', desc: 'caso alguma informação esteja incompleta, incorreta ou desatualizada, você pode pedir que façamos a correção;' },
                        { icon: 'tabler-shield-x', title: 'Anonimização, bloqueio ou eliminação', desc: 'se identificarmos que algum dado seu está sendo tratado de forma excessiva, desnecessária ou em desacordo com a lei, você pode solicitar que ele seja anonimizado (ou seja, tornado impossível de identificar), bloqueado ou excluído;' },
                        { icon: 'tabler-trash', title: 'Eliminação dos dados', desc: 'se você nos deu consentimento para tratar seus dados e quiser que eles sejam apagados, pode solicitar a exclusão exceto quando a lei exigir sua manutenção (por exemplo, para fins legais ou fiscais);' },
                        { icon: 'tabler-share', title: 'Informações sobre compartilhamento', desc: 'você tem o direito de saber com quais empresas ou entidades públicas os seus dados foram compartilhados;' },
                        { icon: 'tabler-info-circle', title: 'Informações sobre o consentimento', desc: 'você pode saber, de forma clara, que o fornecimento de dados não é obrigatório e quais são as consequências de optar por não os fornecer;' },
                        { icon: 'tabler-x', title: 'Revogação do consentimento', desc: 'você pode mudar de ideia e retirar o consentimento que nos deu para tratar seus dados, a qualquer momento.' },
                      ]"
                      :key="index"
                      cols="12"
                      md="6"
                    >
                      <VCard
                        variant="outlined"
                        class="h-100"
                      >
                        <VCardText>
                          <div class="d-flex align-center mb-2">
                            <VIcon
                              color="primary"
                              class="me-2"
                            >
                              {{ direito.icon }}
                            </VIcon>
                            <span class="font-weight-medium text-body-2">({{ ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii'][index] }}) {{ direito.title }}</span>
                          </div>
                          <p class="text-body-2 mb-0">
                            {{ direito.desc }}
                          </p>
                        </VCardText>
                      </VCard>
                    </VCol>
                  </VRow>

                  <VAlert
                    type="success"
                    class="my-4"
                  >
                    Para exercer qualquer um desses direitos, basta entrar em contato com o nosso canal de atendimento, cujas
                    informações estão disponíveis ao final desta política.
                  </VAlert>

                  <p class="text-body-1 mb-4">
                    5.2. Você está ciente de que, embora possa exercer todos os direitos garantidos pela LGPD, o exercício de
                    determinados direitos como a revogação do consentimento, a eliminação de dados ou a limitação do tratamento pode
                    impactar sua experiência na plataforma, podendo inclusive limitar funcionalidades ou inviabilizar o uso da Eventos
                    Connect, já que alguns dados são essenciais para o funcionamento adequado dos serviços oferecidos.
                  </p>
                </VCardText>
              </VCard>

              <!-- Seção VI - Compartilhamento -->
              <VCard
                id="compartilhamento"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-share
                  </VIcon>
                  VI. COMPARTILHAMENTO DE DADOS COM TERCEIROS
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    6.1. Para atender às finalidades descritas nesta Política de Privacidade, a Eventos Connect poderá compartilhar
                    Dados Pessoais com terceiros, incluindo operadores e sub-operadores de dados. A MKT Connect não comercializa Dados
                    Pessoais para fins financeiros. Todos os terceiros que tiverem acesso aos dados estarão vinculados por obrigações
                    contratuais de confidencialidade e segurança da informação, incluindo a adoção de medidas técnicas e organizacionais
                    adequadas para garantir um nível apropriado de proteção.
                  </p>

                  <p class="text-body-1 mb-4">
                    6.2. O compartilhamento de dados poderá ocorrer com os seguintes terceiros:
                  </p>

                  <VList class="mb-4">
                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">I</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Empresas do mesmo grupo econômico
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Empresas do mesmo grupo econômico da MKT Connect, para fins administrativos, operacionais, ações de marketing,
                        atendimento ou em casos de sucessão empresarial, como fusão, aquisição ou incorporação;
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">II</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Prestadores de serviços de tecnologia
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Prestadores de serviços de tecnologia e infraestrutura, como empresas de hospedagem, computação em nuvem,
                        manutenção de sistemas, consultorias em Tl e ferramentas de automação de marketing;
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">III</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Plataformas de pagamento
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Plataformas intermediadoras de pagamento e instituições financeiras, para viabilizar a contratação de planos
                        e processar transações;
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">IV</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Autoridades públicas
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Autoridades públicas, órgãos reguladores ou entidades legais, sempre que houver obrigação legal, ordem
                        judicial, investigação administrativa, requisição de autoridade competente ou necessidade de resguardar os direitos
                        da MKT Connect, da plataforma ou de terceiros;
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">V</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Empresas parceiras
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Empresas envolvidas em eventos conjuntos ou serviços integrados, quando o compartilhamento for necessário para
                        a entrega de serviços ofertados em parceria;
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">VI</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Parceiros comerciais e fornecedores
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Parceiros comerciais e fornecedores, sempre que necessário para a execução dos serviços contratados, como
                        intermediação de pagamentos, serviços técnicos ou operacionais, suporte e comunicação institucional;
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">VII</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Mediante solicitação expressa
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Mediante solicitação expressa do próprio titular dos dados, ou com o seu consentimento específico para
                        finalidades determinadas;
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VAvatar
                          color="pink"
                          size="small"
                        >
                          <span class="text-white text-caption">VIII</span>
                        </VAvatar>
                      </template>
                      <VListItemTitle class="font-weight-medium">
                        Situações específicas
                      </VListItemTitle>
                      <VListItemSubtitle>
                        Em situações específicas, como descumprimento dos Termos de Uso, suspeitas fundadas de fraude ou
                        investigações legítimas, desde que haja base legal para o tratamento.
                      </VListItemSubtitle>
                    </VListItem>
                  </VList>
                </VCardText>
              </VCard>

              <!-- Seção VII - Retenção -->
              <VCard
                id="retencao"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-clock
                  </VIcon>
                  VII. PRAZO DE RETENÇÃO E DESCARTE DE DADOS PESSOAIS
                </VCardTitle>
                <VCardText class="pa-6">
                  <p class="text-body-1 mb-4">
                    7.1. A Eventos Connect manterá os dados pessoais dos usuários apenas pelo tempo necessário para cumprir as
                    finalidades específicas para as quais foram coletados, garantir o funcionamento e a manutenção das contas, atender a
                    obrigações legais ou regulatórias, responder a ordens judiciais, investigações administrativas ou obrigações
                    contratuais, bem como proteger os interesses legítimos da MKT Connect, como prevenir fraudes, mitigar riscos,
                    promover a segurança da plataforma e investigar possíveis violações aos Termos de Uso.
                  </p>

                  <p class="text-body-1 mb-4">
                    7.2. Mesmo que o usuário solicite a exclusão dos dados ou o encerramento da conta, algumas informações poderão ser
                    mantidas, conforme permitido por lei, especialmente quando forem necessárias para o cumprimento de obrigações
                    legais, regulatórias ou a proteção de direitos da empresa. O período de retenção pode variar conforme o tipo de dado
                    e o motivo do tratamento, sempre respeitando o tempo estritamente necessário e a base legal aplicável.
                  </p>

                  <p class="text-body-1 mb-4">
                    7.3. Após o término do prazo de retenção, os dados serão eliminados ou anonimizados, conforme os critérios
                    definidos na Lei Geral de Proteção de Dados Pessoais (LGPD), utilizando métodos seguros que evitem o acesso não
                    autorizado, a restauração ou a reidentificação indevida das informações.
                  </p>

                  <p class="text-body-1 mb-4">
                    Abaixo, apresentamos a tabela com os principais prazos de retenção e os respetivos fundamentos legais aplicáveis.
                  </p>

                  <VTable class="mb-4">
                    <thead>
                      <tr>
                        <th class="text-left bg-grey-lighten-3">
                          CATEGORIA
                        </th>
                        <th class="text-left bg-grey-lighten-3">
                          PERÍODO DE RETENÇÃO
                        </th>
                        <th class="text-left bg-grey-lighten-3">
                          DESCARTES
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="text-body-2">
                          Dados cadastrais (nome, CPF/CNPJ, e-mail, telefone, etc.)<br>
                          Dados relacionados ao uso do planner (eventos, cronograma, lista de convidados)
                        </td>
                        <td class="text-body-2">
                          Enquanto durar a relação contratual + 5 anos após o término<br>
                          Enquanto durar a conta ativa ou até solicitação de exclusão
                        </td>
                        <td class="text-body-2">
                          Eliminação segura por meios digitais ou físicos.<br>
                          Eliminação Digital ou exclusão direta do sistema.
                        </td>
                      </tr>
                      <tr>
                        <td class="text-body-2">
                          Dados para comunicações de marketing
                        </td>
                        <td class="text-body-2">
                          Até a revogação do consentimento ou oposição do titular
                        </td>
                        <td class="text-body-2">
                          Eliminação Digital ou exclusão direta do sistema
                        </td>
                      </tr>
                      <tr>
                        <td class="text-body-2">
                          Dados para prevenção a fraudes e segurança da plataforma.
                        </td>
                        <td class="text-body-2">
                          Até 5 anos após o encerramento da relação contratual
                        </td>
                        <td class="text-body-2">
                          Eliminação Digital ou exclusão direta do sistema
                        </td>
                      </tr>
                      <tr>
                        <td class="text-body-2">
                          Dados anonimizados para fins estatísticos e de pesquisa.
                        </td>
                        <td class="text-body-2">
                          Indeterminadamente art. 12, §4º da LGPD<br>
                          Desde que irreversivelmente anonimizados.
                        </td>
                        <td class="text-body-2">
                          Eliminação Digital ou exclusão direta do sistema
                        </td>
                      </tr>
                    </tbody>
                  </VTable>
                </VCardText>
              </VCard>

              <!-- Seção VIII - Disposições Gerais -->
              <VCard
                id="disposicoes"
                class="mb-6"
                elevation="2"
              >
                <VCardTitle class="bg-primary">
                  <VIcon class="me-2">
                    tabler-gavel
                  </VIcon>
                  VIII. DISPOSIÇÕES GERAIS
                </VCardTitle>
                <VCardText class="pa-6">
                  <VExpansionPanels class="mb-4">
                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <VIcon class="me-2">
                          tabler-refresh
                        </VIcon>
                        <strong>8.1. Alteração Futuras desta Política de Privacidade</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        <p class="text-body-2 mb-0">
                          Esta política de privacidade pode ser alterada a qualquer momento. Quaisquer alterações relevantes especialmente aquelas que impactem seus direitos ou a forma como utilizamos
                          os seus dados serão comunicadas por meio dos nossos canais oficiais ou diretamente em nosso site, junto com a
                          atualização da data de revisão. Ao continuar utilizando nossos serviços após a publicação de uma nova versão desta
                          política, entenderemos que você está ciente e de acordo com as alterações realizadas.
                        </p>
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <VIcon class="me-2">
                          tabler-shield-check
                        </VIcon>
                        <strong>8.2. Segurança da informação e limitação de responsabilidade</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        <p class="text-body-2 mb-2">
                          Embora a Eventos Connect adote medidas técnicas e organizacionais adequadas para proteger os dados pessoais, nos termos da LGPD e das boas práticas de segurança da
                          informação, não é possível garantir a completa imunidade contra incidentes externos, como ataques de hackers,
                          acessos indevidos por terceiros mal-intencionados, falhas técnicas, vícios operacionais, acesso a sites suspeitos ou instalação de softwares maliciosos
                          por parte do usuário.
                        </p>
                        <p class="text-body-2 mb-0">
                          8.2.1. Em situações dessa natureza, a MKTConnect não poderá ser responsabilizada por eventuais danos causados pelo
                          acesso, divulgação ou uso indevido de informações, e nenhuma indenização será devida, exceto nos casos expressamente
                          previstos em lei.
                        </p>
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <VIcon class="me-2">
                          tabler-headphones
                        </VIcon>
                        <strong>8.3. Canal de atendimento ao titular de dados</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        <p class="text-body-2 mb-0">
                          Em caso de dúvidas ou perguntas sobre esta política de privacidade,
                          você poderá entrar em contato pelo canal de suporte [inserir e-mail de contato], ou diretamente na plataforma, por
                          meio do chat de suporte. O horário de atendimento é de segunda à sexta-feira, das 9h00 às 17h00.
                        </p>
                      </VExpansionPanelText>
                    </VExpansionPanel>

                    <VExpansionPanel>
                      <VExpansionPanelTitle>
                        <VIcon class="me-2">
                          tabler-scale
                        </VIcon>
                        <strong>9.1. Lei Aplicável</strong>
                      </VExpansionPanelTitle>
                      <VExpansionPanelText>
                        <p class="text-body-2 mb-0">
                          Para dirimir quaisquer dúvidas relativas ao conteúdo desta Política de Privacidade, fica desde
                          já estabelecida a aplicação das leis da República Federativa do Brasil, independentemente do país de onde for
                          acessado o Curso.
                        </p>
                      </VExpansionPanelText>
                    </VExpansionPanel>
                  </VExpansionPanels>

                  <VAlert
                    type="info"
                    class="mb-4"
                  >
                    <div class="d-flex align-center">
                      <VIcon class="me-2">
                        tabler-info-circle
                      </VIcon>
                      <div>
                        <p class="mb-2 font-weight-medium">
                          Informações da Empresa
                        </p>
                        <p class="text-body-2 mb-0">
                          Esta Política de Privacidade é um documento que descreve como a MKT Connect LTDA ("MKT Connect" ou "Empresa"),
                          pessoa jurídica de direito privado, inscrita no CNPJ sob nº 01.945.991/0001 48, com endereço fiscal à Alameda do
                          Inga, 585, Apt 1901, torre 8, Vale Sereno, na Cidade de Nova Lima, Estado de Minas Gerais, inscrita no CNPJ sob o
                          trata os dados pessoais coletados durante a sua interação com os serviços oferecidos por meio da plataforma Eventos
                          Connect.
                        </p>
                      </div>
                    </div>
                  </VAlert>

                  <VCard
                    variant="outlined"
                    class="mb-4"
                  >
                    <VCardText class="text-center">
                      <VIcon
                        size="48"
                        color="primary"
                        class="mb-2"
                      >
                        tabler-mail
                      </VIcon>
                      <p class="text-h6 mb-2">
                        Dúvidas ou Suporte?
                      </p>
                      <p class="text-body-2 mb-2">
                        Se você tiver qualquer dúvida sobre o conteúdo desta Política ou sobre como utilizamos seus dados pessoais, entre
                        em contato com o nosso canal de atendimento:
                      </p>
                      <VChip
                        color="primary"
                        size="large"
                      >
                        <VIcon start>
                          tabler-mail
                        </VIcon>
                        comercial@eventosconnect.com.br
                      </VChip>
                    </VCardText>
                  </VCard>
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
        </VContainer>
      </VContainer>
    </VMain>
  </VApp>
</template>
